import { useEffect, useRef } from "react";
import { cycleTabFocus } from "../../../../utils/focus-trap";

/**
 * Makes a panel rendered inside `.event-editor-overlay` behave like the modal dialog it visually
 * already is.
 *
 * <p>The overlay is `position: fixed; inset: 0` over a dimmed calendar, but the panel inside it was
 * a plain `&lt;aside&gt;`: no dialog role, no focus trap, and focus never moved in or came back. A
 * keyboard user tabbed off the panel's last control straight into the calendar behind the dim
 * backdrop, and a screen reader read the whole page as though the panel were not there.</p>
 *
 * Returns a ref to spread onto the panel element, which must also carry
 * `role="dialog" aria-modal="true" tabIndex={-1}`.
 *
 * @param onClose Called on Escape. Keep it stable (`useCallback`) — an inline arrow re-registers
 *   the listener on every parent render.
 */
export function useOverlayDialog(onClose: () => void) {
  const dialogRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    // Remember who opened us, so focus goes back there rather than to the top of the document.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    dialog?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "Tab" && dialogRef.current) {
        cycleTabFocus(dialogRef.current, event);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // Only restore if focus is still somewhere in the panel; if the user has already clicked
      // elsewhere, yanking it back would be the more surprising behaviour.
      if (previouslyFocused?.isConnected && dialogRef.current?.contains(document.activeElement)) {
        previouslyFocused.focus();
      }
    };
  }, [onClose]);

  return dialogRef;
}
